import type { ReactNode } from 'react'
import cc from 'classcat'

type Props = Readonly<{
  /** Groups the header radios of one chooser; sections sharing a name form one radio group. */
  radioName: string
  label: ReactNode
  active: boolean
  onActivate: () => void
  children: ReactNode
  className?: string
  headerClassName?: string
  bodyClassName?: string
}>

/**
 * Radio-headed section that expands its body while active and collapses it otherwise.
 * Provides only the structure: radio semantics, the expand animation, and hiding
 * the collapsed body from the accessibility tree. Visual styling comes from the
 * consumer via the class props.
 */
export function ExpandableRadioSection({
  radioName,
  label,
  active,
  onActivate,
  children,
  className,
  headerClassName,
  bodyClassName,
}: Props) {
  return (
    <section className={cc(['expandable-radio-section', className, { active }])}>
      <label className={cc(['expandable-radio-section-header', headerClassName])}>
        <input type="radio" name={radioName} checked={active} onChange={onActivate} />
        <span>{label}</span>
      </label>
      <div
        className={cc(['expandable-radio-section-body', bodyClassName])}
        aria-hidden={!active}
        // The pinned react-dom canary drops boolean inert values; only the
        // empty-string form reaches the DOM, so @types/react's boolean-typed
        // prop needs the cast.
        inert={!active ? ('' as any) : undefined}
      >
        {children}
      </div>
    </section>
  )
}
